import React from 'react';
import { isMobile } from '@/lib/responsive';
import Media from 'react-media';
import cx from 'classnames';

import type { WCategoryView, WFacetView, WShopSearchEntityView } from '@zola/svc-web-api-ts-client';
import { UserContext } from '@/actions/UserActions.type';
// @ts-expect-error types not yet defined
import FilterPillList from '@zola/component-shopping/src/components/common/shop/ShopFilters/FilterPillList/FilterPillList';
// @ts-expect-error types not yet defined
import NoProductResult from '@zola/component-shopping/src/components/shop/NoProductResult/NoProductResult';
import { PageControls } from './PageControls';
import { Sidebar } from './Sidebar';
import { BottomSpinner } from './BottomSpinner';
import { Products } from './Products';

import { MEDIA_SCREEN_SIZES } from '../../constants';

type BodyProps = {
  activeCategoryKey: string;
  areMobileFiltersOpen: boolean;
  categoryFilter: WCategoryView[];
  customTitle: string;
  endOfShopTilesSpinner: React.RefObject<HTMLDivElement>;
  eventData: {
    location?: string;
    section?: string;
  };
  facets: WFacetView[];
  firstPage: number;
  frontTaxId: string | null;
  handleCategorySelect: (category: WCategoryView) => void;
  handleClearAll: () => void;
  handleFilterSelect: (value: Record<string, Record<string, boolean>>) => void;
  handleLoadPreviousProducts: () => void;
  handleMarketingModulesRedirect: (index: number) => void;
  handleSortValueChange: (value: string) => void;
  handleTileSelect: (index: number) => void;
  hasCategoryLink: boolean;
  hasMoreItems: boolean;
  showNoResultsContent: boolean;
  hasNoSearchResults: boolean;
  hasTopProductsLoading: boolean;
  isCategoryPage: boolean;
  isRectangularImage: boolean;
  marketingModules: MarketingModuleLookup | null;
  metaData?: {
    title?: string;
  };
  newFilteringTitle: string;
  openSections: Record<string, boolean>;
  products: WShopSearchEntityView[];
  productTotal: number;
  selectedFilters: Record<string, Record<string, boolean>>;
  selectedSortValue: string;
  setFilters: (value: Record<string, Record<string, boolean>>) => void;
  setOpenSections: (openSections: Record<string, boolean>) => void;
  shopEventData: Record<string, unknown>;
  toggleMobileFilters: () => void;
  transactionIdsMap: Record<number, unknown>;
  useGridTiles?: boolean;
  userContext: UserContext | null;
};
/**
 * PLP body - contains optional sidebar, controls (sort by, mobile filters), and product tiles
 */
export const Body = ({
  activeCategoryKey,
  areMobileFiltersOpen,
  categoryFilter,
  customTitle,
  endOfShopTilesSpinner,
  eventData,
  facets,
  firstPage,
  frontTaxId,
  handleCategorySelect,
  handleClearAll,
  handleFilterSelect,
  handleLoadPreviousProducts,
  handleMarketingModulesRedirect,
  handleSortValueChange,
  handleTileSelect,
  hasCategoryLink,
  hasMoreItems,
  showNoResultsContent,
  hasNoSearchResults,
  hasTopProductsLoading,
  isCategoryPage,
  isRectangularImage,
  marketingModules,
  metaData,
  newFilteringTitle,
  openSections,
  products,
  productTotal,
  selectedFilters,
  selectedSortValue,
  setFilters,
  setOpenSections,
  shopEventData,
  toggleMobileFilters,
  transactionIdsMap,
  useGridTiles,
  userContext,
}: BodyProps) => {
  const optionSelected = Object.keys(selectedFilters || {}).length > 0;
  const hideSidebar =
    (!categoryFilter?.length && !facets?.length) || productTotal === 0 || !!hasNoSearchResults;
  const sidebarClasses = cx('zola-shop-sidebar', {
    'col-md-3 col-lg-3': !useGridTiles,
    'is-hidden': hideSidebar,
  });
  const productsContainerClasses =
    (useGridTiles && 'zola-shop-products-container') ||
    (hideSidebar && 'col-xs-12 col-md-10 col-md-offset-1') ||
    'col-xs-12 col-md-9 col-lg-10';

  const detectDevice = isMobile() ? isMobile() : false;

  return (
    <>
      <div className={sidebarClasses}>
        <div id="js-shop-sidebar" className="sidebar__inner">
          <Media query={`(min-width: ${MEDIA_SCREEN_SIZES.DESKTOP_MIN})`}>
            <div>
              {!hideSidebar && (
                <Sidebar
                  isCategoryPage={isCategoryPage}
                  activeCategoryKey={activeCategoryKey}
                  useGridTiles={useGridTiles}
                  categoryFilter={categoryFilter}
                  facets={facets}
                  selectedFilters={selectedFilters}
                  openSections={openSections}
                  frontTaxId={frontTaxId}
                  handleCategorySelect={handleCategorySelect}
                  handleClearAll={handleClearAll}
                  setOpenSections={setOpenSections}
                  setFilters={setFilters}
                  handleFilterSelect={handleFilterSelect}
                />
              )}
            </div>
          </Media>
        </div>
      </div>
      <div id="js-shop-products-container" className={productsContainerClasses}>
        <div className="title-static-container">
          <PageControls
            areMobileFiltersOpen={areMobileFiltersOpen}
            customTitle={customTitle}
            facets={facets}
            handleClearAll={handleClearAll}
            handleFilterSelect={handleFilterSelect}
            handleSortValueChange={handleSortValueChange}
            hasNoSearchResults={hasNoSearchResults}
            newFilteringTitle={newFilteringTitle}
            productTotal={productTotal}
            selectedFilters={selectedFilters}
            selectedSortValue={selectedSortValue}
            setFilters={setFilters}
            toggleMobileFilters={toggleMobileFilters}
            useGridTiles={useGridTiles}
            userContext={userContext}
          />
          {optionSelected && (
            <div className="pills__container container--desktop">
              <FilterPillList
                selectedFilters={selectedFilters}
                onClearAll={handleClearAll}
                isMobile={detectDevice}
                filterList={facets}
                onFilterSelect={handleFilterSelect}
                setFilters={setFilters}
              />
            </div>
          )}
        </div>
        {products && (
          <Products
            activeCategoryKey={activeCategoryKey}
            categoryFilter={categoryFilter}
            eventData={eventData}
            firstPage={firstPage}
            handleLoadPreviousProducts={handleLoadPreviousProducts}
            handleMarketingModulesRedirect={handleMarketingModulesRedirect}
            handleTileSelect={handleTileSelect}
            hasCategoryLink={hasCategoryLink}
            hasTopProductsLoading={hasTopProductsLoading}
            isRectangularImage={isRectangularImage}
            marketingModules={marketingModules}
            metaData={metaData}
            products={products}
            shopEventData={shopEventData}
            transactionIdsMap={transactionIdsMap}
            useGridTiles={useGridTiles}
          />
        )}
        {productTotal === 0 && showNoResultsContent && (
          <NoProductResult onClearAll={optionSelected ? handleClearAll : undefined} />
        )}
        <BottomSpinner
          hasMoreItems={hasMoreItems}
          productTotal={productTotal}
          useGridTiles={useGridTiles}
          endOfShopTilesSpinner={endOfShopTilesSpinner}
        />
      </div>
    </>
  );
};
